/* © 2024-2026 Johannes Roob. Proprietärer Code. Keine Nutzung oder Weitergabe ohne Erlaubnis. | Proprietary code. No use or distribution without permission. */

import { TimeFormatter } from "../../util/TimeFormatter.js";
import { mustProperty, mustQuerySelector, mustDataset, mustMap, mustClosest, must, mustError } from "../../util/must.js";


export class DataGrid {
    #element;
    #body: HTMLElement;
    #table: HTMLElement;
    #tableHead: HTMLElement;
    #tableBody: HTMLElement;
    #reloadButton: HTMLElement;
    #cellEditButton: HTMLButtonElement | null;
    #footerResultText: HTMLElement;
    #footerProgressRing: HTMLElement;
    #footerStatusIcon: HTMLElement;
    #footerStatusText: HTMLElement;
    #tmplRow: HTMLElement;
    #endpoint: string;
    #auditPrimaryKey: string = "";
    #associationId: string = "";
    #associativePrimaryKey: string = "";

    #tmplRowCells: Map<string, HTMLElement> = new Map(); // Key: string node index
    #timeFormatters: Map<string, TimeFormatter> = new Map(); // Key: string layout
    #dataRows: Map<number, object | null> = new Map(); // Key: index; Value: JSON row from endpoint -> data.rows (JSON)
    #rowSelection: Set<number> = new Set(); // Key: index;
    #rowElements: HTMLElement[] = [];
    #eventController = new AbortController();

    // firstFetch():
    #primaryKeyFields: string[] = [];
    #rowHeight = 0;
    #maxLimit = 0;
    #totalRowCount = 0;
    #maxEndIndex = 0;
    #locale: { Result: string, Results: string; } | undefined;

    #invertSelection = false;
    #lastScrollTop = 0;

    // For column resizing.
    #offsetX = 0;
    #startWidth = 0;
    #animationFrameId: number | null = null;
    #index = 0;

    constructor(element: HTMLElement) {
        if (!element.classList.contains("DataGrid")) {
            throw new Error("this is not a DataGrid");
        }

        this.#element = element;
        this.#body = mustQuerySelector(element, "div.DataGrid-Body");;
        this.#table = mustQuerySelector(element, "div.DataGrid-Table");
        this.#tableHead = mustQuerySelector(element, "div.DataGrid-TableHead");
        this.#tableBody = mustQuerySelector(element, "div.DataGrid-TableBody");
        this.#reloadButton = mustQuerySelector(element, ".DataGrid-ReloadButton");
        this.#cellEditButton = element.querySelector(".DataGrid-CellEditButton");
        this.#footerResultText = mustQuerySelector(element, "div.DataGrid-ResultText");
        this.#footerProgressRing = mustQuerySelector(element, "div.DataGrid-ProgressRing");
        this.#footerStatusIcon = mustQuerySelector(element, "div.DataGrid-StatusIcon");
        this.#footerStatusText = mustQuerySelector(element, "div.DataGrid-StatusText");

        // tmplRow
        const tmplRow: HTMLTemplateElement = mustQuerySelector(element, "template.DataGrid-RowTemplate");
        this.#tmplRow = must(tmplRow.content.firstElementChild, HTMLElement);

        this.#tmplRow.querySelectorAll<HTMLElement>("div.dg-td").forEach(element => {
            // Store cell element for faster lookup.
            const nodeIndex = mustDataset(element, "nodeIndex");
            this.#tmplRowCells.set(nodeIndex, element);
            // Init TimeFormatter for each dateFormat, if specified.
            const layout = element.dataset.dateFormat;

            if (layout !== undefined) {
                const timeFormatter = new TimeFormatter(layout);
                this.#timeFormatters.set(layout, timeFormatter);
            }
        });

        this.#endpoint = mustDataset(element, "endpoint");

        if (element.dataset.auditPrimaryKey !== undefined) {
            this.#auditPrimaryKey = element.dataset.auditPrimaryKey;
        }

        if (element.dataset.associationId !== undefined) {
            this.#associationId = element.dataset.associationId;
        }

        if (element.dataset.associativePrimaryKey !== undefined) {
            this.#associativePrimaryKey = element.dataset.associativePrimaryKey;
        }
    }

    async init() {
        await this.reload();
        this.addEvents();
    }

    destroy() {
        this.#eventController.abort();
        this.#rowElements = [];
        this.#rowSelection.clear();
        this.#dataRows.clear();
        this.#timeFormatters.clear();
        this.#tmplRowCells.clear();
    }

    async reload() {
        this.#dataRows.clear();
        this.#rowSelection.clear();
        this.#invertSelection = false;
        await this.firstFetch();
        const indexRange = this.getRenderRange();
        await this.fetchData(indexRange[0], indexRange[1]);
        this.render();
    }

    getUrl() {
        const url = new URL(this.#endpoint, window.location.origin);
        const params = url.searchParams;
        const searchValue = this.getSearchValue();

        if (searchValue !== "") {
            params.set("Find", searchValue);
        }

        if (this.#auditPrimaryKey !== "") {
            params.set("AuditPrimaryKey", this.#auditPrimaryKey);
        }

        if (this.#associationId !== "") {
            params.set("AssociationId", this.#associationId);
        }

        if (this.#associativePrimaryKey !== "") {
            params.set("AssociativePrimaryKey", this.#associativePrimaryKey);
        }

        return url;
    }

    async firstFetch() {
        this.showProgressRing();
        // Fetch first set of rows.
        const url = this.getUrl();
        let data;

        console.log(`DataGrid: firstFetch: ${url.toString()}`);

        try {
            data = await fetchJson(url);
        } catch (error) {
            const e: Error = mustError(error);
            console.error(e.message);
            this.setStatusText(e);
            this.hideProgressRing();
            return;
        }

        // Set defaults.
        this.#primaryKeyFields = data.primaryKeyFields;
        this.#rowHeight = data.rowHeight;
        this.#maxLimit = data.maxLimit;
        this.#totalRowCount = data.totalRowCount;
        this.#maxEndIndex = this.#totalRowCount - 1;
        this.#locale = data.locale;
        // Store row data.
        this.storeRows(data.rows, 0);
        // Set result / row count text in DataGrid footer.
        this.updateResultText();
        // Set height of element that is being scrolled.
        this.#table.style.height = this.#totalRowCount * this.#rowHeight + "px";
        this.#lastScrollTop = 0;
        this.setStatusText("");
        this.hideProgressRing();
    }

    async fetchData(startIndex: number, endIndex: number) {
        const magic = 42;

        let start = startIndex - magic;
        start = Math.floor(start / magic) * magic;

        if (start < 0) {
            start = 0;
        }

        let end = endIndex + magic;
        end = Math.ceil(end / magic) * magic;

        if (end > this.#maxEndIndex) {
            end = this.#maxEndIndex;
        }

        const range = this.requireRows(start, end);

        if (range === null) {
            return;
        }

        start = range[0];
        end = range[1];
        const limit = end - start + 1;

        if (limit < magic) {
            if (end < this.#maxEndIndex) {
                return; // Do not fetch if row count is below magic.
            }
        }

        this.reserveRows(start, end);
        this.showProgressRing();
        const url = this.getUrl();
        const params = url.searchParams;
        params.set("Offset", start.toString());
        params.set("Limit", limit.toString());
        let data;

        console.log(`DataGrid: fetchData: ${url.toString()}
            offset ${start}, limit ${limit}, index range (${start} - ${end})
            requested index range (${startIndex} - ${endIndex})`);

        try {
            data = await fetchJson(url);
        } catch (error) {
            const e: Error = mustError(error);
            console.error(e.message);
            this.setStatusText(e);
            this.hideProgressRing();
            return;
        }

        // Store row data.
        this.storeRows(data.rows, start);
        this.setStatusText("");
        this.hideProgressRing();
    }

    storeRows(rows: any, startIndex: number) {
        if (rows === null) {
            return;
        }

        rows.forEach((row: any, index: number) => {
            this.#dataRows.set(startIndex + index, row);
        });
    }

    reserveRows(startIndex: number, endIndex: number) {
        for (let i = startIndex; i < endIndex; i++) {
            this.#dataRows.set(i, null);
        }
    }

    requireRows(startIndex: number, endIndex: number) {
        let startIndex2 = null;

        for (let i = startIndex; i <= endIndex; i++) {
            if (!this.#dataRows.has(i)) {
                startIndex2 = i;
                break;
            }
        }

        if (startIndex2 === null) {
            return null;
        }

        let endIndex2 = null;

        for (let i = endIndex; i > startIndex2; i--) {
            if (!this.#dataRows.has(i)) {
                endIndex2 = i;
                break;
            }
        }

        if (endIndex2 === null) {
            return [startIndex2, endIndex];
        }

        return [startIndex2, endIndex2];
    }

    getRenderRange() {
        const scrollTop = this.#body.scrollTop;
        const viewportHeight = this.#body.clientHeight;
        const ratio = scrollTop / (this.#totalRowCount * this.#rowHeight);
        // Determine start index.
        let startIndex = Math.floor(ratio * this.#totalRowCount);

        if (startIndex > this.#maxEndIndex) {
            startIndex = this.#maxEndIndex;
        }

        // Determine visible row count.
        let visibleRowCount = Math.ceil(viewportHeight / this.#rowHeight);

        if (visibleRowCount > this.#maxLimit) {
            visibleRowCount = this.#maxLimit;
        }

        // Determine end index.
        let endIndex = startIndex + visibleRowCount;

        if (endIndex > this.#maxEndIndex) {
            endIndex = this.#maxEndIndex;
        }

        return [startIndex, endIndex];
    }

    render() {
        const indexRange = this.getRenderRange();
        const startIndex = indexRange[0];
        const endIndex = indexRange[1];
        this.fetchData(startIndex, endIndex);
        // Move/scroll grid to match position of scroll element.
        this.#tableBody.style.transform = `translateY(${startIndex * this.#rowHeight}px)`;

        // Hide all rows.
        this.#rowElements.forEach(element => {
            element.style.display = "none";
        });

        let index = 0;

        for (let i = startIndex; i <= endIndex; i++) {
            const rowElement = this.requireRowElement(index);
            const rowData = this.#dataRows.get(i);

            if (rowData === undefined) {
                //console.log(`skipping: ${i}: undefined`);
                continue; // Data not fetched yet. Following calls to render() will display data.
            }

            if (rowData === null) {
                //console.log(`skipping: ${i}: null`);
                continue; // Reserved before fetching data.
            }

            rowElement.dataset.index = i.toString();
            const checkbox = rowElement.querySelector<HTMLInputElement>('input[type="checkbox"]');

            if (checkbox !== null) {
                const has = this.#rowSelection.has(i);
                checkbox.checked = this.#invertSelection ? !has : has;
            }

            this.setRowData(rowElement, rowData);
            rowElement.style.display = "";
            index = index + 1;
        }
    }

    requireRowElement(index: number) {
        if (!this.#rowElements[index]) {
            const element = this.#tmplRow.cloneNode(true) as HTMLElement;
            this.#tableBody.appendChild(element);
            this.#rowElements[index] = element;
        }

        return this.#rowElements[index];
    }

    setRowData(rowElement: HTMLElement, rowData: any) {
        rowElement.querySelectorAll<HTMLElement>("div.dg-td").forEach(cellElement => {
            const namesDot = mustDataset(cellElement, "node");
            const nodeIndex = mustDataset(cellElement, "nodeIndex");
            const fieldType = cellElement.dataset.fieldType;
            const value = getNestedValue(rowData, namesDot);
            let customText = cellElement.dataset.text;

            if (customText !== undefined) {
                if (cellElement.dataset.textHasArgs !== undefined) {
                    const filledText = fillPlaceholders(customText, rowData);
                    customText = filledText;
                }
            }

            if (cellElement.dataset.styleHasArgs !== undefined) {
                const tmplCellElement = mustMap(this.#tmplRowCells, nodeIndex);
                const style = tmplCellElement.getAttribute("style");
                const filledStyle = fillPlaceholders(style, rowData);
                cellElement.setAttribute("style", filledStyle);
            }

            const innerCellElement: HTMLElement = mustQuerySelector(cellElement, "div.dg-td-inner");

            if (innerCellElement.dataset.styleHasArgs !== undefined) {
                const tmplCellElement = mustMap(this.#tmplRowCells, nodeIndex);
                const tmplInnerCellElement = mustQuerySelector(tmplCellElement, "div.dg-td-inner");
                const style = tmplInnerCellElement.getAttribute("style");
                const filledStyle = fillPlaceholders(style, rowData);
                innerCellElement.setAttribute("style", filledStyle);
            }

            switch (fieldType) {
                case "date":
                case "dateTime": {
                    const layout = cellElement.dataset.dateFormat;

                    if (layout === undefined) {
                        innerCellElement.textContent = value;
                    } else {
                        const timeFormatter = mustMap(this.#timeFormatters, layout);
                        innerCellElement.textContent = timeFormatter.format(value);
                    }

                    break;
                }

                case "file": {
                    const button: HTMLButtonElement = mustQuerySelector(cellElement, "button.HyperlinkButton");
                    button.style.display = value === "" ? "none" : "";
                    button.textContent = customText === undefined ? value : customText;
                    break;
                }

                case "color": {
                    const infoBadge = mustQuerySelector(cellElement, "div.InfoBadge");
                    infoBadge.style.backgroundColor = value === "" ? "transparent" : value;

                    if (customText !== undefined) {
                        infoBadge.textContent = customText;
                    }

                    break;
                }

                case "boolean": {
                    const checkBox = mustQuerySelector(cellElement, "div.CheckBox");
                    const checked = value === true;

                    if (checked) {
                        checkBox.dataset.checked = "";
                    } else {
                        delete checkBox.dataset.checked;
                    }

                    if (customText !== undefined) {
                        checkBox.textContent = customText;
                    }

                    break;
                }

                case "primaryKey": {
                    const button: HTMLButtonElement = mustQuerySelector(cellElement, "button.HyperlinkButton");
                    button.textContent = customText === undefined ? value : customText;
                    break;
                }

                default: {
                    innerCellElement.textContent = customText === undefined ? value : customText;
                    break;
                }
            }
        });
    }

    getSelection() {
        const primaryKeys: string[] = [];

        this.#rowSelection.forEach(index => {
            const primaryKey = this.getPrimaryKey(index);
            primaryKeys.push(primaryKey);
        });

        return {
            InvertSelection: this.#invertSelection,
            PrimaryKey: primaryKeys
        };
    }

    getCell(any: HTMLElement) {
        const td = mustClosest(any, "div.dg-td");
        const tr = mustClosest(td, "div.dg-tr");
        const nodeIndex = mustDataset(td, "nodeIndex");
        const index: number = Number(mustDataset(tr, "index"));
        const primaryKey = this.getPrimaryKey(index);

        return {
            PrimaryKey: primaryKey,
            NodeIndex: nodeIndex
        };
    }

    // Param index must be of type "number".
    getPrimaryKey(index: number): string {
        const rowData = this.#dataRows.get(index);
        const values: any[] = [];

        this.#primaryKeyFields.forEach((item, index) => {
            values[index] = getNestedValue(rowData, item);
        });

        return JSON.stringify(values);
    }

    selectAllRows() {
        this.#invertSelection = true;
        this.#rowSelection.clear();
        this.render();
    }

    unselectAllRows() {
        this.#invertSelection = false;
        this.#rowSelection.clear();
        this.render();
    }

    getSearchValue() {
        const form = mustClosest(this.#element, "form");
        const input: HTMLInputElement = mustQuerySelector(form, 'input[name="SearchValue"]');
        return input.value;
    }

    showProgressRing() {
        this.#footerProgressRing.style.opacity = "1";
    }

    hideProgressRing() {
        this.#footerProgressRing.style.opacity = "";
    }

    updateResultText() {
        if (this.#locale === undefined) {
            throw new Error("DataGrid: locale is undefined");
        }

        const resultText = this.#totalRowCount === 1 ? this.#locale.Result : this.#locale.Results;
        this.#footerResultText.textContent = this.#totalRowCount + " " + resultText;
    }

    setStatusText(status: string | Error) {
        if (status instanceof Error) {
            this.#footerStatusIcon.classList.remove("Informational");
            this.#footerStatusIcon.classList.add("Error");
            this.#footerStatusText.textContent = status.name + ":" + status.message;
            this.#footerStatusIcon.style.display = "";
            this.#footerStatusText.style.display = "";
            return;
        }

        if (status === "") {
            this.#footerStatusIcon.style.display = "none";
            this.#footerStatusText.style.display = "none";
            return;
        }

        this.#footerStatusIcon.classList.remove("Error");
        this.#footerStatusIcon.classList.add("Informational");
        this.#footerStatusText.textContent = status;
        this.#footerStatusIcon.style.display = "";
        this.#footerStatusText.style.display = "";
    }

    addEvents() {
        // Click reload button.
        const eventReload = () => {
            this.reload();
        };

        this.#reloadButton.addEventListener("click", eventReload,
            {
                passive: true,
                signal: this.#eventController.signal
            });

        // Select row/unselect row.
        const eventSelect = (e: Event) => {
            const input = must(e.target, HTMLInputElement);

            if (input.type === "checkbox") {
                const tr = mustClosest(input, "div.dg-tr");
                const index = Number(mustDataset(tr, "index"));

                if (input.checked === this.#invertSelection) {
                    this.#rowSelection.delete(index);
                } else {
                    this.#rowSelection.add(index);
                }
            }
        };

        this.#tableBody.addEventListener("change", eventSelect,
            {
                passive: true,
                signal: this.#eventController.signal
            });

        // Show edit button in hovered cell.
        if (this.#cellEditButton !== null) {
            const eventCellEditMouseOver = (e: Event) => {
                if (this.#cellEditButton === null) {
                    return;
                }

                const target = must(e.target, HTMLElement);
                const td = target.closest<HTMLElement>("div.dg-td");

                if (td === null) {
                    return;
                }

                if (td.dataset.allowEdit === undefined) {
                    return;
                }

                const button = this.#cellEditButton;

                if (button.parentNode === td) {
                    return;
                }

                button.style.display = "";
                td.appendChild(button);
            };

            this.#tableBody.addEventListener("mousemove", eventCellEditMouseOver,
                {
                    passive: true,
                    signal: this.#eventController.signal
                });
        }

        // While scrolling data grid call render function.
        const eventScroll = () => {
            const scrollTop = this.#body.scrollTop;

            if (scrollTop === this.#lastScrollTop) {
                return;
            }

            this.#lastScrollTop = scrollTop;

            requestAnimationFrame(() => {
                this.render();
            });
        };

        this.#body.addEventListener("scroll", eventScroll,
            {
                passive: true,
                signal: this.#eventController.signal
            });

        // Resize columns.
        this.addEventsColumnResize();
    }

    addEventsColumnResize() {
        const resizePointerMove = (e: PointerEvent) => {
            if (this.#animationFrameId !== null) {
                return;
            }

            this.#animationFrameId = requestAnimationFrame(() => {
                const delta = e.clientX - this.#offsetX;
                const newWidth = Math.max(65, this.#startWidth + delta) + "px";
                setColumnWidth(this.#tableBody, this.#index, newWidth);
                setColumnWidth(this.#tableHead, this.#index, newWidth);
                this.#animationFrameId = null;
            });
        };

        const resizePointerUp = (e: PointerEvent) => {
            const handle = must(e.target, HTMLElement);
            handle.classList.remove("Active");
            handle.releasePointerCapture(e.pointerId);
            handle.removeEventListener("pointermove", resizePointerMove);
            handle.removeEventListener("pointerup", resizePointerUp);
            handle.removeEventListener("pointercancel", resizePointerUp);
            handle.removeEventListener("lostpointercapture", resizePointerUp);

            if (this.#animationFrameId !== null) {
                cancelAnimationFrame(this.#animationFrameId);
                this.#animationFrameId = null;
            }
        };

        const resizePointerDown = (e: PointerEvent) => {
            if (e.button !== 0 && e.pointerType !== "touch") {
                return;
            }

            const handle = must(e.target, HTMLElement);
            const th = mustClosest(handle, "div.dg-th");
            const tr = mustClosest(handle, "div.dg-tr");
            const index = [...tr.children].indexOf(th);
            this.#offsetX = e.clientX;
            this.#startWidth = th.getBoundingClientRect().width;
            this.#index = index;
            handle.classList.add("Active");
            handle.setPointerCapture(e.pointerId);
            handle.addEventListener("pointermove", resizePointerMove,
                {
                    passive: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointerup", resizePointerUp,
                {
                    passive: true,
                    once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("pointercancel", resizePointerUp,
                {
                    passive: true,
                    once: true,
                    signal: this.#eventController.signal
                });

            handle.addEventListener("lostpointercapture", resizePointerUp,
                {
                    passive: true,
                    once: true,
                    signal: this.#eventController.signal
                });
        };

        const resizeDblClick = (e: MouseEvent) => {
            setColumnWidth(this.#tableBody, this.#index, "auto");
            setColumnWidth(this.#tableHead, this.#index, "auto");
            const bodyWidth = parseFloat(getColumnWidth(this.#tableBody, this.#index));
            const headWidth = parseFloat(getColumnWidth(this.#tableHead, this.#index));
            const width = Math.max(bodyWidth, headWidth);
            setColumnWidth(this.#tableBody, this.#index, width + "px");
            setColumnWidth(this.#tableHead, this.#index, width + "px");
        };

        // Add resize event listeners for each column resize handle.
        this.#element.querySelectorAll<HTMLElement>("div.DataGrid-ColumnResizeHandler").forEach(element => {
            // Resize column width.
            element.addEventListener("pointerdown", resizePointerDown,
                {
                    passive: true,
                    signal: this.#eventController.signal
                });

            // Double click/tap auto-resize column width.
            element.addEventListener("dblclick", resizeDblClick,
                {
                    passive: true,
                    signal: this.#eventController.signal
                });
        });
    }
}

function setColumnWidth(grid: HTMLElement, index: number, newWidth: string) {
    const columns = grid.style.gridTemplateColumns.trim().split(/\s+(?![^()]*\))/);
    columns[index] = newWidth;
    grid.style.gridTemplateColumns = columns.join(" ");
}

function getColumnWidth(grid: HTMLElement, index: number) {
    const columns = getComputedStyle(grid).gridTemplateColumns.split(/\s+/);
    return columns[index];
}

function getNestedValue(obj: any, path: string) {
    // obj: JSON object
    // path: string e.g. "User.Name"
    return path.split(".").reduce((currentObj, currentPathSegment) => {
        if (currentObj === null || currentObj === undefined) {
            return undefined;
        }

        return currentObj[currentPathSegment];
    }, obj);
}

function fillPlaceholders(text: string | null, data: any): string {
    if (text === null) {
        return "";
    }

    return text.replace(/\[\[\s*([^\]\]]+?)\s*\]\]/g, (_, path) => {
        const value = getNestedValue(data, path);
        return value === undefined ? "" : value;
    });
}

async function fetchJson(url: URL) {
    const timeout = 30_000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
        const response = await fetch(url);

        if (response.ok === false) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const contentType = response.headers.get("content-type");

        if (contentType === null) {
            throw new Error("Content-Type is missing");
        }

        if (contentType.includes("application/json") === false) {
            throw new Error(`Expected JSON, got ${contentType}`);
        }

        return await response.json();

    } catch (error) {
        const e: Error = mustError(error);

        if (e.name === "AbortError") {
            throw new Error(`Request timed out after ${timeout}ms`);
        }

        throw e;

    } finally {
        clearTimeout(timeoutId);
    }
}